import { useCallback, useEffect, useRef, useState } from "react";
import { playEndAlert, primeAudio, resumeAudio } from "../utils/restTimerAudio";
import {
  cancelRestTimerAlarm,
  nextAlarmSequence,
  scheduleRestTimerAlarm,
} from "../utils/restTimerAlarmApi";

const MIN_SECONDS = 0;
const MAX_SECONDS = 3600;
const TICK_MS = 250;

export interface IRestTimerStartArgs {
  planExerciseId: number;
  exerciseName: string;
  durationSeconds: number;
}

export interface IRestTimerStopArgs {
  /** When provided, only stop if the active timer matches this exercise. */
  ifPlanExerciseId?: number;
}

export interface IRestTimerState {
  isActive: boolean;
  planExerciseId: number | null;
  exerciseName: string;
  /** Whole-number seconds remaining (rounded up so 0.4s still reads as "1"). */
  remainingSeconds: number;
  /** Full duration the user configured for this run, in seconds. */
  totalSeconds: number;
}

const INITIAL_STATE: IRestTimerState = {
  isActive: false,
  planExerciseId: null,
  exerciseName: "",
  remainingSeconds: 0,
  totalSeconds: 0,
};

export interface IUseRestTimerOptions {
  /** This device's push endpoint, or null when it cannot get notifications. */
  getPushEndpoint?: () => string | null;
  /** Called as a rest starts, inside the set-check tap. */
  onStart?: () => void;
}

interface IPendingAlarm {
  endpoint: string;
  sequence: number;
}

const clampSeconds = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  if (value < MIN_SECONDS) return MIN_SECONDS;
  if (value > MAX_SECONDS) return MAX_SECONDS;
  return Math.round(value);
};

/**
 * Holds an in-browser countdown that one exercise card can drive at a time.
 * The timer is intentionally NOT persisted — it's a transient UI affordance,
 * the same way Hevy / Strong handle it. The configured *duration* is what gets
 * persisted (via `updateExerciseRestTimer`); the running countdown is local.
 *
 * Why a wall-clock reference (`endAtRef`) and not a counter? Because intervals
 * drift, throttle on background tabs, and pause when the screen is locked on
 * mobile. Reading from `Date.now()` on every tick keeps the displayed time
 * honest no matter what the browser does to our setInterval.
 *
 * A locked phone freezes the page, so nothing here can ring then. When the app
 * leaves the screen mid-rest we ask the server to push to this device at the
 * end instead, and cancel that push when the app comes back on screen. While a
 * push is pending the page does not ring by itself, so the rest never rings
 * twice.
 */
export const useRestTimer = (options: IUseRestTimerOptions = {}) => {
  const [state, setState] = useState<IRestTimerState>(INITIAL_STATE);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endAtRef = useRef<number | null>(null);
  // Latch so we only fire the end-alert once per run, even if React renders
  // twice or the user mashes buttons at the end.
  const endedRef = useRef(false);
  const exerciseNameRef = useRef("");
  // The push asked for while the app is off screen, until it is cancelled or due.
  const alarmRef = useRef<IPendingAlarm | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const clearInterval_ = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const cancelPendingAlarm = useCallback(() => {
    const alarm = alarmRef.current;
    if (!alarm) return;
    alarmRef.current = null;
    void cancelRestTimerAlarm({ endpoint: alarm.endpoint, sequence: nextAlarmSequence() });
  }, []);

  const stop = useCallback(
    (args?: IRestTimerStopArgs) => {
      setState((current) => {
        if (!current.isActive) return current;
        if (
          args?.ifPlanExerciseId !== undefined &&
          current.planExerciseId !== args.ifPlanExerciseId
        ) {
          return current;
        }
        clearInterval_();
        cancelPendingAlarm();
        endAtRef.current = null;
        endedRef.current = true;
        return INITIAL_STATE;
      });
    },
    [clearInterval_, cancelPendingAlarm],
  );

  const start = useCallback(
    ({ planExerciseId, exerciseName, durationSeconds }: IRestTimerStartArgs) => {
      const total = clampSeconds(durationSeconds);
      if (total <= 0) return;

      // We're being called from inside a user gesture (set-check) — perfect
      // moment to unlock mobile audio so the alert tone can play later.
      primeAudio();
      optionsRef.current.onStart?.();

      clearInterval_();
      cancelPendingAlarm();
      endedRef.current = false;
      exerciseNameRef.current = exerciseName;
      const now = Date.now();
      endAtRef.current = now + total * 1000;

      setState({
        isActive: true,
        planExerciseId,
        exerciseName,
        remainingSeconds: total,
        totalSeconds: total,
      });

      intervalRef.current = setInterval(() => {
        if (endAtRef.current === null) return;
        const remainingMs = endAtRef.current - Date.now();
        const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
        setState((current) => {
          if (!current.isActive) return current;
          if (current.remainingSeconds === remainingSeconds) return current;
          return { ...current, remainingSeconds };
        });
        if (remainingMs <= 0) {
          // Latch the end-of-run alert so we don't double-fire.
          if (!endedRef.current) {
            endedRef.current = true;
            // Off screen with a push on its way: the push rings, not the page.
            if (!alarmRef.current) playEndAlert();
          }
          // The server sends the push now; there is nothing left to cancel.
          alarmRef.current = null;
          clearInterval_();
          endAtRef.current = null;
          // Auto-dismiss the bar a moment after the alert so the user sees "00:00".
          setTimeout(() => {
            setState((current) => (current.isActive ? INITIAL_STATE : current));
          }, 1500);
        }
      }, TICK_MS);
    },
    [clearInterval_, cancelPendingAlarm],
  );

  const adjust = useCallback((deltaSeconds: number) => {
    setState((current) => {
      // After the timer reaches zero we clear `endAtRef` and queue a 1.5s
      // auto-dismiss. During that grace period adjustments are intentionally
      // ignored — the rest is "done" and any further action belongs to the
      // next set.
      if (!current.isActive || endAtRef.current === null) return current;
      const now = Date.now();
      const projectedRemaining = Math.max(
        0,
        Math.ceil((endAtRef.current - now) / 1000) + deltaSeconds,
      );
      const clamped = Math.min(MAX_SECONDS, projectedRemaining);
      // Move the wall-clock target — the running interval will recompute the
      // displayed remaining on its next tick.
      endAtRef.current = now + clamped * 1000;
      return {
        ...current,
        remainingSeconds: clamped,
        // Buffing the timer above its original duration should widen the
        // progress bar's baseline, not shrink it.
        totalSeconds: Math.max(current.totalSeconds, clamped),
      };
    });
  }, []);

  const skip = useCallback(() => {
    stop();
  }, [stop]);

  // The app leaves the screen (phone locked, another app): from now on only a
  // push can ring, so ask for one at the end of the rest.
  const requestAlarm = useCallback(() => {
    const endAt = endAtRef.current;
    if (endAt === null || alarmRef.current) return;
    const remainingMs = endAt - Date.now();
    if (remainingMs <= 0) return;
    const endpoint = optionsRef.current.getPushEndpoint?.() ?? null;
    if (!endpoint) return;

    const alarm: IPendingAlarm = { endpoint, sequence: nextAlarmSequence() };
    alarmRef.current = alarm;
    void scheduleRestTimerAlarm({
      endpoint,
      remainingMs,
      exerciseName: exerciseNameRef.current,
      sequence: alarm.sequence,
    }).then((scheduled) => {
      // Nobody will push: the page rings by itself, if it still runs.
      if (!scheduled && alarmRef.current === alarm) alarmRef.current = null;
    });
  }, []);

  // Back on screen: the page can ring again, so the push is called off.
  const onBackOnScreen = useCallback(() => {
    const endAt = endAtRef.current;
    if (endAt !== null) resumeAudio();

    const alarm = alarmRef.current;
    if (!alarm) return;
    alarmRef.current = null;
    if (endAt === null || Date.now() >= endAt) {
      // The rest ended while the phone was locked and the push already rang;
      // the countdown catching up must not ring a second time.
      endedRef.current = true;
      return;
    }
    void cancelRestTimerAlarm({ endpoint: alarm.endpoint, sequence: nextAlarmSequence() });
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        requestAlarm();
      } else {
        onBackOnScreen();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [requestAlarm, onBackOnScreen]);

  // Unmount cleanup — releases the interval (and a pending push) if the
  // provider tears down mid-rest.
  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
      cancelPendingAlarm();
    };
  }, [cancelPendingAlarm]);

  return {
    state,
    start,
    stop,
    adjust,
    skip,
  };
};

export type RestTimerController = ReturnType<typeof useRestTimer>;
